/* eslint-disable no-loop-func */
/**
 * B080060 出荷確定
 */
import { Column, Query } from "@material-table/core";
import { Box } from "@mui/material";
import {
  B000223BatchNm,
  B080060Check,
  B080060CheckRequestList,
  B080060CheckResponse,
  B080060Find,
  B080060FindRequest,
  B080060Update,
  B080060UpdateRequest,
  catchRestError,
} from "clients/rest";
import {
  msgState,
  SCodeSelect,
  SConfirmDialog,
  SConfirmDialogAction,
  SDateField,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  SText,
  STextField,
} from "components";
import { getClientError, getClientMessage } from "messages";
import { getSCode, SCode, Shukajokyo } from "messages/codes";
import { labels } from "messages/labels";
import { B000223 } from "pages";
import { createRef, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { chunk, date, dateWithSlashAt, mapIds } from "services";
import { B080060shukajokyo, cancelKbn, suryoteisei } from "services/codes";
import { focusState, useFocus } from "states";

// 呼び出し時のプロパティ
type Props = Partial<{
  // 呼び出し元へデータを送る場合に設定する。
  handleSubmit: (data: any) => void;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  shukashijiYmdFrom: string;
  shukashijiYmdTo: string;
  shijikaisu: string;
  batchNo: string;
  batchNm: string;
  shukajokyo: SCode;
  dialogHikiateMessage: string;
  dialogToriokiMessage: string;
}>;

// テーブルデータ定義
type Data = Partial<{
  shukashijiYmd: string;
  shijikaisu: string;
  batchNo: string;
  batchNm: string;
  tenposu: number;
  shohinsu: number;
  denpyomaisu: number;
  shukajokyo: number;
  cancelKbn: number;
  suryoteisei: number;
  version: string;
  shukajokyoView: number;
  cancelKbnView: number;
  suryoteiseiView: number;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  shukashijiYmdFrom: date(), // 当日日付
  shukashijiYmdTo: date(), // 当日日付
  shijikaisu: "",
  batchNo: "",
  batchNm: "",
  shukajokyo: undefined,
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  {
    field: "shukashijiYmd",
    title: "出荷指示日",
    width: "4rem",
    render: (data) => {
      if (data.shukashijiYmd! !== null && data.shukashijiYmd! !== "") {
        return dateWithSlashAt(data.shukashijiYmd!);
      }
    },
  },
  {
    field: "shijikaisu",
    title: "指示回数　",
    width: "3rem",
    align: "right",
  },
  {
    field: "batchNo",
    title: "バッチ番号　",
    width: "3rem",
    align: "right",
  },
  { field: "batchNm", title: "バッチ名", width: "6rem" },
  {
    field: "tenposu",
    title: "店舗数　",
    width: "4rem",
    align: "right",
  },
  {
    field: "shohinsu",
    title: "商品数　",
    width: "4rem",
    align: "right",
  },
  {
    field: "denpyomaisu",
    title: "伝票枚数　",
    width: "4rem",
    align: "right",
  },
  {
    field: "shukajokyoView",
    title: "出荷状況",
    width: "3rem",
    render: (data) => {
      let color = "";

      // 出荷状況 未処理 赤
      if (data.shukajokyo === B080060shukajokyo.mishori) {
        color = "#FF0000";
        return (
          <Box sx={{ color: color }}>
            {getSCode(Shukajokyo, data.shukajokyo.toString() ?? "0")?.label}
          </Box>
        );

        // 出荷状況 指示済 青
      } else if (data.shukajokyo === B080060shukajokyo.shijizumi) {
        color = "#0000FF";
        return (
          <Box sx={{ color: color }}>
            {getSCode(Shukajokyo, data.shukajokyo.toString() ?? "1")?.label}
          </Box>
        );

        // 出荷状況 確定済 緑
      } else if (data.shukajokyo === B080060shukajokyo.kakuteizumi) {
        color = "#008000";
        return (
          <Box sx={{ color: color }}>
            {getSCode(Shukajokyo, data.shukajokyo.toString() ?? "2")?.label}
          </Box>
        );
      }
    },
  },
  { field: "shukajokyo", hidden: true },
  {
    field: "cancelKbnView",
    title: "キャンセル有",
    width: "4rem",
    align: "center",
    render: (data) => {
      if (data.cancelKbn === cancelKbn.sumi) {
        return "◯";
      } else if (data.cancelKbn === cancelKbn.zen) {
        return "全";
      }
      return "";
    },
  },
  { field: "cancelKbn", hidden: true },
  {
    field: "suryoteiseiView",
    title: "数量訂正有",
    width: "4rem",
    align: "center",
    render: (data) => {
      if (data.suryoteisei === suryoteisei.exists) {
        return "◯";
      }
      return "";
    },
  },
  { field: "suryoteisei", hidden: true },
  { field: "version", hidden: true },
];

// フォーカスIDの設定
const focusId = "B080060";
// API通信分割のための分割数
const bunkatusu = 20;

export const B080060 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 状態管理の初期設定
  // テーブルの値取得用Ref
  const tableRef = createRef();

  // 初回検索用のフラグ
  const [searched, setSearched] = useState(false);

  // 検索ボタン押下時に初期ページにするステート
  const isDefaultPage = useRef(false);

  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });

  // selectboxで選択された行のデータ
  const [selectedData, setSelectedData] = useState<Data[]>([]);

  // ボタンの活性・非活性
  const [disabledExecute, setDisabledExecute] = useState(true);

  // メッセージ表示
  const setMsg = useSetRecoilState(msgState);

  // 画面遷移
  const navigate = useNavigate();

  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));

  // チェック後の確認ダイアログオープン用
  const [openCheckedConfirmHikiate, setOpenCheckedConfirmHikiate] =
    useState(false);
  const [openCheckedConfirmTorioki, setOpenCheckedConfirmTorioki] =
    useState(false);

  // 初期表示イベント
  useEffect(() => {
    // 初回フォーカス先
    focus(1);
  });

  // テーブル行チェックボックス選択時
  const handleCheckTableRow = (data: Data[]) => {
    // 行が選択されている場合のみ実行する。
    const disabled = data.length <= 0;
    setDisabledExecute(disabled);
    setSelectedData(data);
  };

  // 実行ボタン押下時チェック処理呼出しイベント
  const handleCheck = () => {
    // リクエスト作成
    const param: B080060CheckRequestList = {
      shukabatchList: selectedData.map((value) => {
        return {
          shukashijiYmd: value.shukashijiYmd!,
          shijikaisu: value.shijikaisu!,
          batchNo: value.batchNo!,
        };
      }),
    };

    // API呼出し
    B080060Check(param)
      .then((value) => {
        handleOpenCheckedConfirm(value.data);
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // チェック後ダイアログ制御
  const handleOpenCheckedConfirm = (data: B080060CheckResponse) => {
    if (data.hikiateCheckFlg === false || data.toriokiCheckFlg === false) {
      if (data.hikiateCheckFlg === false && data.toriokiCheckFlg === true) {
        methods.setValue("dialogHikiateMessage", data.hikiateWarnMessage);
        setOpenCheckedConfirmHikiate(true);
      } else if (
        data.toriokiCheckFlg === false &&
        data.hikiateCheckFlg === true
      ) {
        methods.setValue("dialogToriokiMessage", data.toriokiWarnMessage);
        setOpenCheckedConfirmTorioki(true);
      } else if (
        data.hikiateCheckFlg === false &&
        data.toriokiCheckFlg === false
      ) {
        methods.setValue("dialogHikiateMessage", data.hikiateWarnMessage);
        setOpenCheckedConfirmHikiate(true);
        methods.setValue("dialogToriokiMessage", data.toriokiWarnMessage);
        setOpenCheckedConfirmTorioki(true);
      }
    } else if (data.hikiateCheckFlg === true && data.toriokiCheckFlg === true) {
      handleExecute();
    }
  };

  // OK押下時
  const handleOkCheckedConfirmHikiate = () => {
    setOpenCheckedConfirmHikiate(false);
    handleExecute();
  };

  //キャンセル押下時
  const handleCancelCheckedConfirmHikiate = () => {
    setOpenCheckedConfirmHikiate(false);
  };

  // OK押下時
  const handleOkCheckedConfirmTorioki = () => {
    setOpenCheckedConfirmTorioki(false);
    handleExecute();
  };

  //キャンセル押下時
  const handleCancelCheckedConfirmTorioki = () => {
    setOpenCheckedConfirmTorioki(false);
  };

  // 実行ボタンクリックイベント
  const handleExecute = () => {
    // API通信分割のため
    const chunked = chunk(
      selectedData.map((value) => {
        return {
          shukashijiYmd: value.shukashijiYmd,
          shijikaisu: value.shijikaisu,
          batchNo: value.batchNo,
          version: value.version,
        };
      }),
      bunkatusu
    );
    (async () => {
      let r: any = undefined;
      for (const chunk of chunked) {
        if (r) break;

        // リクエスト作成
        const param: B080060UpdateRequest = {
          shukabatchList: chunk,
        };
        // API呼び出し
        await B080060Update(param).catch((reason) => {
          r = reason;
        });
      }

      // thenとcatchの内容
      // テーブル再検索
      (tableRef.current as any).onQueryChange();
      // ボタン非活性化
      setDisabledExecute(true);
      // 選択されているデータを空にする
      setSelectedData([]);
      if (r) {
        catchRestError(r, navigate, setMsg);
      } else {
        // 完了メッセージ
        setMsg(getClientMessage("B080060_001"));
      }
    })();
  };

  // 検索時の初期化処理
  const handleResetSearch = () => {
    // ボタン非活性化
    setDisabledExecute(true);
    // 選択されているデータを空にする
    setSelectedData([]);
    (tableRef.current as any).handleClearSelect();
  };

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    // 初期化
    handleResetSearch();
    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }

      // 検索フォームのデータを取得する。
      const fields = methods.getValues();

      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;

      // 検索パラメータを作成する。
      const param: B080060FindRequest = {
        shukashijiYmdFrom: fields.shukashijiYmdFrom!,
        shukashijiYmdTo: fields.shukashijiYmdTo,
        shijikaisu: fields.shijikaisu,
        batchNo: fields.batchNo,
        shukajokyo: fields.shukajokyo?.value,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        // ソートの設定
        // ["出力項目名,順番(asc/desc", ...]
        sort: ["shukashijiYmd,asc", "shijikaisu,asc", "batchNo,asc"],
      };
      // API呼び出し
      B080060Find(param)
        // 正常取得時
        .then((value) => {
          if (value.data.totalElements === 0) {
            setMsg(getClientError("B080060_001"));
          }
          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyoNo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        // エラー時
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
          resolve({
            data: [],
            page: 0,
            totalCount: 0,
          });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="出荷確定"
        gridTemplateColumns="6rem 9rem 1rem 9rem 6rem 10rem 15rem 2rem"
      >
        <SLabel required>出荷指示日</SLabel>
        <SDateField
          name="shukashijiYmdFrom"
          {...register(1)}
          required
          label={"出荷指示日(開始)"}
        />
        <SText>～</SText>
        <SDateField name="shukashijiYmdTo" {...register(2)} />
        <SText gridColumn="1">　</SText>
        <SLabel gridColumn="1">指示回数</SLabel>
        <SNumberField name="shijikaisu" size={4} {...register(3)} />
        <SLabel gridColumn={5}>バッチ番号</SLabel>
        <SNumberField
          name="batchNo"
          code
          size={4}
          {...register(4)}
          dialog={<B000223 />}
          handleSubmit={(data: any) => {
            methods.setValue("batchNo", data.batchNo);
            methods.setValue("batchNm", data.batchNm);
          }}
          onBlur={() => B000223BatchNm(methods, navigate, setMsg)}
        />
        <STextField name="batchNm" size={40} disabled />
        <SText gridColumn="1">　</SText>
        <SLabel gridColumn="1">出荷状況</SLabel>
        <SCodeSelect
          name="shukajokyo"
          options={Shukajokyo}
          {...register(5)}
          empty
        />
        <SNormalButton
          label={labels.buttons.search}
          onClick={handleClickSearchButton}
          gridRow={6}
          gridColumn={7}
          {...register(6)}
          align={"right"}
        />
      </SFrame>
      <SFrame>
        <STable
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          onSelectionChange={handleCheckTableRow}
          width={1250}
          height={400}
          pageSize={100}
          multi
          handleSetDisabled={(rowData) =>
            rowData.shukajokyo !== B080060shukajokyo.shijizumi ||
            rowData.cancelKbn === cancelKbn.zen
          }
          onPageChange={handleResetSearch}
        />
      </SFrame>
      <SFrame>
        <SNormalButton
          label={labels.buttons.execute}
          disabled={disabledExecute}
          align="right"
          {...register(7)}
          dialog={
            <SConfirmDialog
              title="出荷確定"
              msg="確定します。よろしいですか？"
            />
          }
          dialogAction="confirm"
          handleSubmit={handleCheck}
        />
      </SFrame>
      <SConfirmDialogAction
        msg={methods.watch("dialogHikiateMessage")!}
        onOk={handleOkCheckedConfirmHikiate}
        onCancel={handleCancelCheckedConfirmHikiate}
        isOpen={openCheckedConfirmHikiate}
      />
      <SConfirmDialogAction
        msg={methods.watch("dialogToriokiMessage")!}
        onOk={handleOkCheckedConfirmTorioki}
        onCancel={handleCancelCheckedConfirmTorioki}
        isOpen={openCheckedConfirmTorioki}
      />
    </FormProvider>
  );
};
